import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TimeWindowPicker, presetToDateRange } from '../components/TimeWindowPicker';
import { ServiceFilter } from '../components/ServiceFilter';
import { KPICard, KPIGrid } from '../components/KPICards';
import { SpendChart } from '../components/SpendChart';
import { CampaignTable } from '../components/CampaignTable';
import { useMetaAdsData } from '../hooks/useMetaAdsData';
import { formatBRL, formatNumber } from '../lib/format';
import type { ServiceLabel, TimeWindow } from '../types';

export default function Overview() {
  const [searchParams, setSearchParams] = useSearchParams();

  const preset = (searchParams.get('preset') ?? '30d') as TimeWindow['preset'];
  const service = (searchParams.get('service') ?? 'all') as ServiceLabel;
  const { startDate, endDate } = presetToDateRange(preset);

  function setPreset(p: TimeWindow['preset']) {
    setSearchParams(sp => { sp.set('preset', p); return sp; });
  }
  function setService(s: ServiceLabel) {
    setSearchParams(sp => { sp.set('service', s); return sp; });
  }

  const { kpis, timeseries, campaigns, isLoading } = useMetaAdsData({
    startDate, endDate, service, account: 'all',
  });

  const blendedCPL = (() => {
    const totalLeads = kpis?.kommo.totalLeads ?? 0;
    const totalSpend = (kpis?.meta.valorUsado ?? 0) + (kpis?.google.valorUsado ?? 0);
    return totalLeads > 0 ? totalSpend / totalLeads : 0;
  })();

  return (
    <div style={{ padding: '28px', maxWidth: '1440px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: 'var(--text-primary)' }}>Overview</h1>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-tertiary)' }}>
            {startDate} → {endDate}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <ServiceFilter value={service} onChange={setService} />
          <TimeWindowPicker value={preset} onChange={setPreset} />
        </div>
      </div>

      {/* KPI Grid */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px', color: 'var(--text-tertiary)' }}>
          <div className="spinner" />
        </div>
      ) : (
        <>
          <KPIGrid columns={3}>
            <KPICard label="Meta — Gasto" value={formatBRL(kpis?.meta.valorUsado ?? 0)} accentColor="var(--accent)" delay={0} />
            <KPICard label="Google — Gasto" value={formatBRL(kpis?.google.valorUsado ?? 0)} accentColor="var(--blue)" delay={0.04} />
            <KPICard label="Gasto Total" value={formatBRL((kpis?.meta.valorUsado ?? 0) + (kpis?.google.valorUsado ?? 0))} delay={0.08} />
            <KPICard label="Total de Leads" value={formatNumber(kpis?.kommo.totalLeads ?? 0)} accentColor="var(--green)" delay={0.12} />
            <KPICard label="Leads Ganhos" value={formatNumber(kpis?.kommo.totalWon ?? 0)} delay={0.16} />
            <KPICard label="CPL Blended" value={formatBRL(blendedCPL)} delay={0.20} />
          </KPIGrid>

          {/* Spend + Leads Chart */}
          <div style={{ marginTop: '24px' }}>
            <SpendChart data={timeseries} showMeta showGoogle showLeads height={220} />
          </div>

          {/* Top Campaigns */}
          <div style={{ marginTop: '24px' }}>
            <h2 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Top Campanhas
            </h2>
            <CampaignTable campaigns={campaigns.slice(0, 10)} showSource />
          </div>
        </>
      )}
    </div>
  );
}
